import React, { useEffect, useState } from 'react';
import {
    getNotificationPreferences,
    updateNotificationPreferences,
    NotificationPreference,
} from '../services/notificationPreferenceApi';
import { toast } from 'react-toastify';
import SecuritySettings from '../components/SecuritySettings';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const PREFERENCE_LABELS: { key: keyof NotificationPreference; label: string; description: string }[] = [
    {
        key: 'emailSiteMonitoring',
        label: 'Monitoramento de Sites',
        description: 'Receber alertas quando um site monitorado apresentar problemas',
    },
    {
        key: 'emailSiteDown',
        label: 'Site Fora do Ar',
        description: 'Receber alerta imediato quando um site ficar offline',
    },
    {
        key: 'emailCampaignReport',
        label: 'Relatório de Campanha',
        description: 'Receber relatórios ao final de cada campanha',
    },
    {
        key: 'emailDailyPerformance',
        label: 'Performance Diária',
        description: 'Receber resumo diário de performance',
    },
    {
        key: 'emailWeeklyReport',
        label: 'Relatório Semanal',
        description: 'Receber relatório semanal consolidado',
    },
];

const Settings = () => {
    const [activeTab, setActiveTab] = useState('notifications');
    const [preferences, setPreferences] = useState<NotificationPreference | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getNotificationPreferences()
            .then(setPreferences)
            .catch(() => toast.error('Erro ao carregar preferências'))
            .finally(() => setLoading(false));
    }, []);

    const handleToggle = async (key: keyof NotificationPreference) => {
        if (!preferences || saving) return;

        const newValue = !preferences[key];
        setSaving(true);

        try {
            const updated = await updateNotificationPreferences({ [key]: newValue });
            setPreferences(updated);
        } catch {
            toast.error('Erro ao salvar preferência');
        } finally {
            setSaving(false);
        }
    };

    document.title = 'Configurações | Ad Pulse';

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                                <div className="flex gap-4">
                                    <div className="w-64 flex-shrink-0">
                                        <TabsList className="flex flex-col h-full w-full p-4 bg-card">
                                            <TabsTrigger value="notifications" className="w-full justify-start data-[state=active]:bg-muted">
                                                <i className="ri-notification-3-line mr-2"></i>
                                                Notificações
                                            </TabsTrigger>
                                            <TabsTrigger value="security" className="w-full justify-start data-[state=active]:bg-muted">
                                                <i className="ri-shield-keyhole-line mr-2"></i>
                                                Segurança
                                            </TabsTrigger>
                                        </TabsList>
                                    </div>
                                    <div className="flex-1">
                                        <TabsContent value="notifications" className="mt-0">
                                            <Card>
                                                <CardHeader>
                                                    <CardTitle>Preferências de Notificação</CardTitle>
                                                    <CardDescription>
                                                        Configure quais alertas e relatórios deseja receber por email.
                                                    </CardDescription>
                                                </CardHeader>
                                                <CardContent>
                                                    {loading ? (
                                                        <div className="text-center py-8">
                                                            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                                                        </div>
                                                    ) : preferences ? (
                                                        <div className="space-y-4">
                                                            {PREFERENCE_LABELS.map(({ key, label, description }) => (
                                                                <div
                                                                    key={key}
                                                                    className="flex items-center justify-between p-4 border rounded-lg"
                                                                >
                                                                    <div>
                                                                        <h6 className="font-medium mb-1">{label}</h6>
                                                                        <p className="text-sm text-muted-foreground">{description}</p>
                                                                    </div>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={() => handleToggle(key)}
                                                                        disabled={saving}
                                                                        className="flex items-center gap-2"
                                                                    >
                                                                        {preferences[key] ? 'Ativado' : 'Desativado'}
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="text-muted-foreground">Não foi possível carregar as preferências.</p>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </TabsContent>
                                        <TabsContent value="security" className="mt-0">
                                            <SecuritySettings />
                                        </TabsContent>
                                    </div>
                                </div>
                            </Tabs>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default Settings;
